import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Calendar, UnlockKeyhole, Pencil, Trash2, ChevronLeft, ChevronRight, Users, CheckSquare, XSquare, MapPin } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks } from 'date-fns';
import { es } from 'date-fns/locale';

function AdminTrainings() {
  const [trainings, setTrainings] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [weekDialogOpen, setWeekDialogOpen] = useState(false);
  const [openSlotsDialogOpen, setOpenSlotsDialogOpen] = useState(false);
  const [openSlotsRange, setOpenSlotsRange] = useState({
    fecha_inicio: format(new Date(), 'yyyy-MM-dd'),
    fecha_fin: format(addWeeks(new Date(), 1), 'yyyy-MM-dd')
  });
  const [formData, setFormData] = useState({
    tipo: 'carrera',
    dia_semana: 'lunes',
    fecha: '',
    hora: '18:30',
    ejercicios: [],
    max_participantes: 12,
    ubicacion_id: '',
    ubicacion_nombre_custom: '',
    ubicacion_maps_custom: '',
    color: '',
    entreno_id: 'none',
    // Acertijo opcional
    acertijo_enunciado: '',
    acertijo_respuesta: '',
    acertijo_consecuencia: '',
    texto_competicion: ''
  });
  const [weeklyConfig, setWeeklyConfig] = useState({
    hora: '18:30',
    max_participantes: 12,
    mes_seleccionado: new Date(),
    dias: [
      { dia: 'lunes', tipo: 'carrera', activo: true, hora: '17:30', ubicacion_id: 'cabana' },
      { dia: 'martes', tipo: 'funcional', activo: true, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'miercoles', tipo: 'funcional', activo: false, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'jueves', tipo: 'funcional', activo: true, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'viernes', tipo: 'competicion', activo: false, hora: '17:30', ubicacion_id: '' },
      { dia: 'sabado', tipo: 'excursion', activo: false, hora: '10:00', ubicacion_id: '' },
      { dia: 'domingo', tipo: 'especial', activo: false, hora: '10:00', ubicacion_id: '' }
    ]
  });
  const [proDialogOpen, setProDialogOpen] = useState(false);
  const [proConfig, setProConfig] = useState({
    max_participantes: 12,
    mes_seleccionado: new Date(),
    dias: [
      { dia: 'lunes', tipo: 'carrera', activo: true, hora: '17:30', ubicacion_id: 'cabana' },
      { dia: 'martes', tipo: 'fuerza', activo: true, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'miercoles', tipo: 'funcional', activo: false, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'jueves', tipo: 'fuerza', activo: true, hora: '18:30', ubicacion_id: 'mk_car' },
      { dia: 'viernes', tipo: 'carrera_mediciones', activo: true, hora: '17:30', ubicacion_id: 'cabana' },
      { dia: 'sabado', tipo: 'excursion', activo: false, hora: '10:00', ubicacion_id: '' },
      { dia: 'domingo', tipo: 'especial', activo: false, hora: '10:00', ubicacion_id: '' }
    ]
  });
  const [editingTraining, setEditingTraining] = useState(null);
  const [entrenosList, setEntrenosList] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selectedTrainings, setSelectedTrainings] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Filtrar entrenamientos por el mes seleccionado
  const filteredTrainings = trainings.filter(training => {
    if (!training.fecha) return false;
    const parts = training.fecha.split('-');
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const targetMonth = viewMonth.getMonth() + 1;
    const targetYear = viewMonth.getFullYear();
    return month === targetMonth && year === targetYear;
  }).sort((a, b) => a.fecha.localeCompare(b.fecha));

  const fetchData = async () => {
    try {
      const [trainingsRes, ubicacionesRes] = await Promise.all([
        axios.get(`${API}/trainings`),
        axios.get(`${API}/trainings/ubicaciones`)
      ]);
      setTrainings(trainingsRes.data);
      setUbicaciones(ubicacionesRes.data || []);
      // Exercises endpoint was removed, using entrenos instead
      setExercises([]);
      try {
        const entrenosRes = await axios.get(`${API}/entrenos`);
        setEntrenosList(entrenosRes.data || []);
      } catch (e2) { /* no-op */ }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/trainings`, { ...formData, entreno_id: formData.entreno_id === 'none' ? null : formData.entreno_id });
      // Si hay acertijo, crearlo justo después
      if (formData.acertijo_enunciado && formData.acertijo_respuesta && res.data?.id) {
        try {
          await axios.post(`${API}/trainings/${res.data.id}/acertijo`, {
            enunciado: formData.acertijo_enunciado,
            respuesta_correcta: formData.acertijo_respuesta,
            consecuencia: formData.acertijo_consecuencia || ''
          });
        } catch (e2) {
          alert('Entreno creado, pero el acertijo falló: ' + (e2.response?.data?.detail || e2.message));
        }
      }
      fetchData();
      resetForm();
      setDialogOpen(false);
      alert('Entreno creado correctamente');
    } catch (error) {
      alert('Error al crear entreno');
    }
  };

  const handleOpenWeeklySlots = async () => {
    try {
      const params = new URLSearchParams({
        fecha_inicio: openSlotsRange.fecha_inicio,
        fecha_fin: openSlotsRange.fecha_fin
      });
      await axios.post(`${API}/trainings/open-weekly?${params}`);
      fetchData();
      setOpenSlotsDialogOpen(false);
      alert(`Plazas abiertas del ${openSlotsRange.fecha_inicio} al ${openSlotsRange.fecha_fin}`);
    } catch (error) {
      alert('Error al abrir plazas');
    }
  };

  const handleCreateWeeklyTrainings = async () => {
    // Usar el mes seleccionado
    const selectedDate = weeklyConfig.mes_seleccionado;
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const diasActivos = weeklyConfig.dias.filter(d => d.activo);
    
    if (diasActivos.length === 0) {
      alert('Debes seleccionar al menos un día');
      return;
    }

    try {
      const dayNameToNumber = {
        'lunes': 1,
        'martes': 2,
        'miercoles': 3,
        'jueves': 4,
        'viernes': 5,
        'sabado': 6,
        'domingo': 0
      };

      let createdCount = 0;

      // Recorrer todos los días del mes seleccionado
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek = currentDate.getDay();
        
        // Verificar si este día de la semana está en la configuración
        const diaConfig = diasActivos.find(d => dayNameToNumber[d.dia] === dayOfWeek);
        
        if (diaConfig) {
          const fecha = format(currentDate, 'yyyy-MM-dd');
          
          // Verificar si ya existe un entreno en esa fecha
          const exists = trainings.some(t => t.fecha === fecha && t.tipo === diaConfig.tipo);
          
          if (!exists) {
            await axios.post(`${API}/trainings`, {
              tipo: diaConfig.tipo,
              dia_semana: diaConfig.dia,
              fecha: fecha,
              hora: diaConfig.hora || weeklyConfig.hora,
              ejercicios: [],
              ubicacion_id: diaConfig.ubicacion_id || ''
            });
            createdCount++;
          }
        }
      }
      
      fetchData();
      setWeekDialogOpen(false);
      alert(`Se crearon ${createdCount} entrenamientos para ${format(selectedDate, 'MMMM yyyy', { locale: es })}`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear entrenamientos del mes');
    }
  };

  const handleCreateProTrainings = async () => {
    const selectedDate = proConfig.mes_seleccionado;
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const diasActivos = proConfig.dias.filter(d => d.activo);
    
    if (diasActivos.length === 0) {
      alert('Debes seleccionar al menos un día');
      return;
    }

    try {
      const dayNameToNumber = {
        'lunes': 1, 'martes': 2, 'miercoles': 3, 'jueves': 4,
        'viernes': 5, 'sabado': 6, 'domingo': 0
      };

      let createdCount = 0;

      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek = currentDate.getDay();
        const diaConfig = diasActivos.find(d => dayNameToNumber[d.dia] === dayOfWeek);
        
        if (diaConfig) {
          const fecha = format(currentDate, 'yyyy-MM-dd');
          const exists = trainings.some(t => t.fecha === fecha && t.tipo === diaConfig.tipo);
          
          if (!exists) {
            await axios.post(`${API}/trainings`, {
              tipo: diaConfig.tipo,
              dia_semana: diaConfig.dia,
              fecha: fecha,
              hora: diaConfig.hora,
              ejercicios: [],
              max_participantes: proConfig.max_participantes,
              ubicacion_id: diaConfig.ubicacion_id || ''
            });
            createdCount++;
          }
        }
      }
      
      fetchData();
      setProDialogOpen(false);
      alert(`Se crearon ${createdCount} entrenamientos PRO para ${format(selectedDate, 'MMMM yyyy', { locale: es })}`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear entrenamientos PRO del mes');
    }
  };

  const handleEditTraining = (training) => {
    setEditingTraining(training);
    setFormData({
      tipo: training.tipo,
      dia_semana: training.dia_semana,
      fecha: training.fecha,
      hora: training.hora,
      ejercicios: training.ejercicios || [],
      max_participantes: training.max_participantes || 12,
      ubicacion_id: training.ubicacion_id || '',
      ubicacion_nombre_custom: training.ubicacion_id === 'otros' ? (training.ubicacion_nombre || '') : '',
      ubicacion_maps_custom: training.ubicacion_id === 'otros' ? (training.ubicacion_maps || '') : '',
      color: training.color || '',
      texto_competicion: training.texto_competicion || '',
      entreno_id: training.entreno_id || 'none'
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTraining = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/trainings/${editingTraining.id}`, { ...formData, entreno_id: formData.entreno_id === 'none' ? null : formData.entreno_id });
      fetchData();
      setEditDialogOpen(false);
      setEditingTraining(null);
      alert('Entreno actualizado correctamente');
    } catch (error) {
      alert('Error al actualizar entreno');
    }
  };

  const handleDeleteTraining = async (trainingId, fecha) => {
    let fechaFormateada = fecha;
    try {
      fechaFormateada = format(new Date(fecha + 'T00:00:00'), "d 'de' MMMM", { locale: es });
    } catch (e) {
      console.error('Error formateando fecha:', e);
    }
    
    if (window.confirm(`¿Estás seguro de eliminar el entreno del ${fechaFormateada}?`)) {
      try {
        const response = await axios.delete(`${API}/trainings/${trainingId}`);
        console.log('Entreno eliminado:', response.data);
        await fetchData();
        alert('Entreno eliminado correctamente');
      } catch (error) {
        console.error('Error al eliminar:', error.response?.data || error.message);
        alert('Error al eliminar entreno: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // Funciones para eliminación en lote
  const toggleTrainingSelection = (trainingId) => {
    const newSelected = new Set(selectedTrainings);
    if (newSelected.has(trainingId)) {
      newSelected.delete(trainingId);
    } else {
      newSelected.add(trainingId);
    }
    setSelectedTrainings(newSelected);
  };

  const selectAllInMonth = () => {
    const ids = filteredTrainings.map(t => t.id);
    setSelectedTrainings(new Set(ids));
  };

  const clearSelection = () => {
    setSelectedTrainings(new Set());
    setIsSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedTrainings.size === 0) {
      alert('No hay entrenamientos seleccionados');
      return;
    }

    const count = selectedTrainings.size;
    if (window.confirm(`¿Estás seguro de eliminar ${count} entrenamiento${count > 1 ? 's' : ''}?`)) {
      setBulkDeleteLoading(true);
      try {
        const response = await axios.post(`${API}/trainings/bulk-delete`, {
          training_ids: Array.from(selectedTrainings)
        });
        await fetchData();
        clearSelection();
        alert(response.data.message);
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar entrenamientos: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkDeleteLoading(false);
      }
    }
  };

  const handleDeleteMonth = async () => {
    const monthStart = format(startOfMonth(viewMonth), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(viewMonth), 'yyyy-MM-dd');
    const monthName = format(viewMonth, 'MMMM yyyy', { locale: es });

    if (window.confirm(`¿Estás seguro de eliminar TODOS los entrenamientos de ${monthName}?\n\nEsta acción no se puede deshacer.`)) {
      setBulkDeleteLoading(true);
      try {
        const response = await axios.post(`${API}/trainings/bulk-delete`, {
          fecha_desde: monthStart,
          fecha_hasta: monthEnd
        });
        await fetchData();
        clearSelection();
        alert(response.data.message);
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar entrenamientos: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkDeleteLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'carrera',
      dia_semana: 'lunes',
      fecha: '',
      hora: '19:00',
      ejercicios: [],
      max_participantes: 12,
      ubicacion_id: '',
      ubicacion_nombre_custom: '',
      ubicacion_maps_custom: '',
      color: '',
      acertijo_enunciado: '',
      acertijo_respuesta: '',
      acertijo_consecuencia: '',
      texto_competicion: '',
      entreno_id: 'none'
    });
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="admin-trainings-title">
              Administración de Entrenamientos
            </h1>
            <p className="text-gray-400">Gestiona los entrenamientos y plazas</p>
            <p className="text-gray-500 text-sm mt-1">
              ⏰ Las plazas se abrirán automáticamente los domingos a las 20:00
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Botones de selección múltiple */}
            {!isSelectionMode ? (
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                onClick={() => setIsSelectionMode(true)}
                data-testid="btn-selection-mode"
              >
                <CheckSquare className="mr-2 h-4 w-4" />
                Seleccionar
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                  onClick={selectAllInMonth}
                  data-testid="btn-select-all"
                >
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Todo el mes ({filteredTrainings.length})
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  onClick={clearSelection}
                  data-testid="btn-clear-selection"
                >
                  <XSquare className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                {selectedTrainings.size > 0 && (
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading}
                    data-testid="btn-delete-selected"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {bulkDeleteLoading ? 'Eliminando...' : `Eliminar (${selectedTrainings.size})`}
                  </Button>
                )}
              </>
            )}

            {/* Botón eliminar mes completo */}
            {filteredTrainings.length > 0 && !isSelectionMode && (
              <Button
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-900/30"
                onClick={handleDeleteMonth}
                disabled={bulkDeleteLoading}
                data-testid="btn-delete-month"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {bulkDeleteLoading ? 'Eliminando...' : 'Borrar Mes'}
              </Button>
            )}

            <Dialog open={weekDialogOpen} onOpenChange={setWeekDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="btn-primary-mk"
                  data-testid="btn-create-weekly"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Crear Mes Completo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gold">Configurar Entrenamientos Mensuales</DialogTitle>
                  <p className="text-gray-400 text-sm mt-2">
                    Se crearán entrenamientos para todo el mes según tu configuración
                  </p>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Selector de mes */}
                  <div className="space-y-2">
                    <Label className="text-lg">Seleccionar Mes</Label>
                    <div className="flex items-center justify-center gap-4 p-3 bg-gray-800 rounded-lg">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWeeklyConfig({
                          ...weeklyConfig,
                          mes_seleccionado: subMonths(weeklyConfig.mes_seleccionado, 1)
                        })}
                        className="text-white hover:text-gold"
                        data-testid="btn-mes-anterior"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <span className="text-xl font-bold text-gold capitalize min-w-[180px] text-center">
                        {format(weeklyConfig.mes_seleccionado, 'MMMM yyyy', { locale: es })}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWeeklyConfig({
                          ...weeklyConfig,
                          mes_seleccionado: addMonths(weeklyConfig.mes_seleccionado, 1)
                        })}
                        className="text-white hover:text-gold"
                        data-testid="btn-mes-siguiente"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hora por Defecto</Label>
                      <Input
                        type="time"
                        value={weeklyConfig.hora}
                        onChange={(e) => setWeeklyConfig({ ...weeklyConfig, hora: e.target.value })}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="input-hora-semanal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Máximo Participantes</Label>
                      <Input
                        type="number"
                        value={weeklyConfig.max_participantes}
                        onChange={(e) => setWeeklyConfig({ ...weeklyConfig, max_participantes: parseInt(e.target.value) })}
                        className="bg-gray-800 border-gray-700 text-white"
                        min="1"
                        max="30"
                        data-testid="input-max-participantes"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-lg">Días de la Semana</Label>
                    {weeklyConfig.dias.map((dia, index) => (
                      <div key={dia.dia} className="p-3 bg-gray-800 rounded-lg space-y-2">
                        <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={dia.activo}
                          onChange={(e) => {
                            const newDias = [...weeklyConfig.dias];
                            newDias[index].activo = e.target.checked;
                            setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                          }}
                          className="w-5 h-5 cursor-pointer flex-shrink-0"
                          data-testid={`checkbox-${dia.dia}`}
                        />
                        <span className="capitalize text-white font-semibold w-20 flex-shrink-0">{dia.dia}</span>
                        <Select
                          value={dia.tipo}
                          onValueChange={(value) => {
                            const newDias = [...weeklyConfig.dias];
                            newDias[index].tipo = value;
                            setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                          }}
                          disabled={!dia.activo}
                        >
                          <SelectTrigger className="bg-gray-900 border-gray-700 flex-1" data-testid={`select-tipo-${dia.dia}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700 text-white">
                            <SelectItem value="carrera">Carrera</SelectItem>
                            <SelectItem value="funcional">Funcional</SelectItem>
                            <SelectItem value="fuerza">Fuerza</SelectItem>
                            <SelectItem value="competicion">Competicion</SelectItem>
                            <SelectItem value="carrera_mediciones">Carrera + Mediciones</SelectItem>
                            <SelectItem value="especial">Especial</SelectItem>
                            <SelectItem value="excursion">Excursion</SelectItem>
                            <SelectItem value="natacion">Natacion</SelectItem>
                            <SelectItem value="bike">Bike</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="time"
                          value={dia.hora}
                          onChange={(e) => {
                            const newDias = [...weeklyConfig.dias];
                            newDias[index].hora = e.target.value;
                            setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                          }}
                          disabled={!dia.activo}
                          className="bg-gray-900 border-gray-700 text-white w-28 flex-shrink-0"
                          data-testid={`input-hora-${dia.dia}`}
                        />
                        </div>
                        <div className="flex items-center gap-3 pl-8">
                          <MapPin className="h-4 w-4 text-gold flex-shrink-0" />
                          <Select
                            value={dia.ubicacion_id || 'none'}
                            onValueChange={(value) => {
                              const newDias = [...weeklyConfig.dias];
                              newDias[index].ubicacion_id = value === 'none' ? '' : value;
                              setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                            }}
                            disabled={!dia.activo}
                          >
                            <SelectTrigger className="bg-gray-900 border-gray-700 flex-1 h-9" data-testid={`select-ubicacion-${dia.dia}`}>
                              <SelectValue placeholder="Sin ubicación" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                              <SelectItem value="none">Sin ubicación</SelectItem>
                              {ubicaciones.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={handleCreateWeeklyTrainings}
                    className="w-full btn-primary-mk"
                    data-testid="btn-confirmar-semana"
                  >
                    Crear Entrenamientos Seleccionados
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Dialog PRO */}
            <Dialog open={proDialogOpen} onOpenChange={setProDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="btn-create-pro-monthly"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Crear Mes Completo PRO
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-purple-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-purple-400">Crear Mes PRO</DialogTitle>
                  <p className="text-gray-400 text-sm mt-2">
                    Lun/Vie 17:30 | Mar/Jue 18:30 | Viernes con mediciones (purpura)
                  </p>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Selector de mes */}
                  <div className="space-y-2">
                    <Label className="text-lg">Seleccionar Mes</Label>
                    <div className="flex items-center justify-center gap-4 p-3 bg-gray-800 rounded-lg">
                      <Button variant="ghost" size="icon"
                        onClick={() => setProConfig({ ...proConfig, mes_seleccionado: subMonths(proConfig.mes_seleccionado, 1) })}
                        className="text-white hover:text-purple-400">
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <span className="text-xl font-bold text-purple-400 capitalize min-w-[180px] text-center">
                        {format(proConfig.mes_seleccionado, 'MMMM yyyy', { locale: es })}
                      </span>
                      <Button variant="ghost" size="icon"
                        onClick={() => setProConfig({ ...proConfig, mes_seleccionado: addMonths(proConfig.mes_seleccionado, 1) })}
                        className="text-white hover:text-purple-400">
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Max Participantes</Label>
                    <Input type="number" value={proConfig.max_participantes}
                      onChange={(e) => setProConfig({ ...proConfig, max_participantes: parseInt(e.target.value) })}
                      className="bg-gray-800 border-gray-700 text-white" min="1" max="30"
                      data-testid="pro-input-max-participantes" />
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-lg">Dias de la Semana PRO</Label>
                    {proConfig.dias.map((dia, index) => (
                      <div key={dia.dia} className={`p-3 rounded-lg space-y-2 ${dia.tipo === 'carrera_mediciones' ? 'bg-purple-900/30 border border-purple-700' : 'bg-gray-800'}`}>
                        <div className="flex items-center gap-3">
                        <input type="checkbox" checked={dia.activo}
                          onChange={(e) => {
                            const newDias = [...proConfig.dias];
                            newDias[index].activo = e.target.checked;
                            setProConfig({ ...proConfig, dias: newDias });
                          }}
                          className="w-5 h-5 cursor-pointer flex-shrink-0"
                          data-testid={`pro-checkbox-${dia.dia}`} />
                        <span className="capitalize text-white font-semibold w-20 flex-shrink-0">{dia.dia}</span>
                        <Select value={dia.tipo}
                          onValueChange={(value) => {
                            const newDias = [...proConfig.dias];
                            newDias[index].tipo = value;
                            setProConfig({ ...proConfig, dias: newDias });
                          }}
                          disabled={!dia.activo}>
                          <SelectTrigger className="bg-gray-900 border-gray-700 flex-1" data-testid={`pro-select-tipo-${dia.dia}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700 text-white">
                            <SelectItem value="carrera">Carrera</SelectItem>
                            <SelectItem value="funcional">Funcional</SelectItem>
                            <SelectItem value="fuerza">Fuerza</SelectItem>
                            <SelectItem value="competicion">Competicion</SelectItem>
                            <SelectItem value="carrera_mediciones">Carrera + Mediciones</SelectItem>
                            <SelectItem value="especial">Especial</SelectItem>
                            <SelectItem value="excursion">Excursion</SelectItem>
                            <SelectItem value="natacion">Natacion</SelectItem>
                            <SelectItem value="bike">Bike</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input type="time" value={dia.hora}
                          onChange={(e) => {
                            const newDias = [...proConfig.dias];
                            newDias[index].hora = e.target.value;
                            setProConfig({ ...proConfig, dias: newDias });
                          }}
                          disabled={!dia.activo}
                          className="bg-gray-900 border-gray-700 text-white w-28 flex-shrink-0"
                          data-testid={`pro-input-hora-${dia.dia}`} />
                        </div>
                        <div className="flex items-center gap-3 pl-8">
                          <MapPin className="h-4 w-4 text-purple-400 flex-shrink-0" />
                          <Select
                            value={dia.ubicacion_id || 'none'}
                            onValueChange={(value) => {
                              const newDias = [...proConfig.dias];
                              newDias[index].ubicacion_id = value === 'none' ? '' : value;
                              setProConfig({ ...proConfig, dias: newDias });
                            }}
                            disabled={!dia.activo}
                          >
                            <SelectTrigger className="bg-gray-900 border-gray-700 flex-1 h-9" data-testid={`pro-select-ubicacion-${dia.dia}`}>
                              <SelectValue placeholder="Sin ubicación" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                              <SelectItem value="none">Sin ubicación</SelectItem>
                              {ubicaciones.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button onClick={handleCreateProTrainings}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="btn-confirmar-pro">
                    Crear Entrenamientos PRO
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Dialog para Abrir Plazas con selector de semana */}
            <Dialog open={openSlotsDialogOpen} onOpenChange={setOpenSlotsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="btn-open-slots"
                >
                  <UnlockKeyhole className="mr-2 h-4 w-4" />
                  Abrir Plazas
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gold">Abrir Plazas para Inscripciones</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-gray-400 text-sm">
                    Selecciona el rango de fechas para abrir las inscripciones:
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Desde</Label>
                      <Input
                        type="date"
                        value={openSlotsRange.fecha_inicio}
                        onChange={(e) => setOpenSlotsRange({...openSlotsRange, fecha_inicio: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hasta</Label>
                      <Input
                        type="date"
                        value={openSlotsRange.fecha_fin}
                        onChange={(e) => setOpenSlotsRange({...openSlotsRange, fecha_fin: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                  {/* Botones de acceso rápido */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        const nextWeek = addWeeks(today, 1);
                        setOpenSlotsRange({
                          fecha_inicio: format(today, 'yyyy-MM-dd'),
                          fecha_fin: format(nextWeek, 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Próximos 7 días
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        const start = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
                        const end = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
                        setOpenSlotsRange({
                          fecha_inicio: format(start, 'yyyy-MM-dd'),
                          fecha_fin: format(end, 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Semana que viene
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        setOpenSlotsRange({
                          fecha_inicio: format(startOfMonth(today), 'yyyy-MM-dd'),
                          fecha_fin: format(endOfMonth(today), 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Este mes
                    </Button>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleOpenWeeklySlots}
                  >
                    <UnlockKeyhole className="mr-2 h-4 w-4" />
                    Abrir Plazas
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary-mk" data-testid="btn-add-training">
                  <Plus className="mr-2 h-4 w-4" /> Nuevo Entreno
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gold">Crear Nuevo Entreno</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo de Entreno</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => setFormData({ ...formData, tipo: value, color: value === 'especial' ? formData.color : '' })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="carrera">Carrera</SelectItem>
                        <SelectItem value="funcional">Funcional</SelectItem>
                        <SelectItem value="fuerza">Fuerza</SelectItem>
                        <SelectItem value="competicion">Competicion</SelectItem>
                        <SelectItem value="carrera_mediciones">Carrera + Mediciones</SelectItem>
                        <SelectItem value="especial">Especial</SelectItem>
                        <SelectItem value="excursion">Excursion</SelectItem>
                        <SelectItem value="natacion">Natacion</SelectItem>
                        <SelectItem value="bike">Bike</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.tipo === 'especial' && (
                      <div className="mt-2 p-2 bg-gray-800/60 rounded-lg border border-gray-700 space-y-2" data-testid="especial-color-picker">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-400">Color del badge (Especial)</Label>
                          {formData.color && (
                            <button type="button" onClick={() => setFormData({ ...formData, color: '' })}
                              className="text-[10px] text-gray-500 hover:text-white underline"
                              data-testid="btn-color-reset">Restablecer</button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {['#a855f7','#ec4899','#f59e0b','#ef4444','#22c55e','#06b6d4','#3b82f6','#fbbf24','#8b5cf6'].map(c => (
                            <button key={c} type="button"
                              onClick={() => setFormData({ ...formData, color: c })}
                              className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                                (formData.color || '#a855f7') === c ? 'border-white ring-2 ring-white/40 scale-110' : 'border-gray-600'
                              }`}
                              style={{ backgroundColor: c }}
                              title={c}
                              data-testid={`color-preset-${c.replace('#','')}`}
                            />
                          ))}
                          <input type="color"
                            value={formData.color || '#a855f7'}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="w-7 h-7 rounded-full bg-transparent border border-gray-600 cursor-pointer p-0"
                            data-testid="input-color-custom"
                            title="Color personalizado"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Entreno del día (solo lo ves tú)</Label>
                    <Select
                      value={formData.entreno_id || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, entreno_id: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-entreno-create">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                        <SelectItem value="none">Sin entreno asignado</SelectItem>
                        {entrenosList.map(en => (
                          <SelectItem key={en.id} value={en.id}>{en.codigo} — {en.dios}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.tipo === 'competicion' && (
                    <div className="space-y-2" data-testid="texto-competicion-create">
                      <Label htmlFor="texto_competicion" className="text-red-300">Texto para el calendario</Label>
                      <Input
                        id="texto_competicion"
                        value={formData.texto_competicion}
                        onChange={(e) => setFormData({ ...formData, texto_competicion: e.target.value })}
                        placeholder="Ej: Travesía Cap Blanc"
                        className="bg-gray-800 border-red-700/40 text-white"
                        maxLength={60}
                        data-testid="input-texto-competicion"
                      />
                      <p className="text-xs text-gray-500">Se muestra en la tarjeta del calendario (máx. 60 caracteres)</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="dia_semana">Día de la Semana</Label>
                    <Select
                      value={formData.dia_semana}
                      onValueChange={(value) => setFormData({ ...formData, dia_semana: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-dia">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="lunes">Lunes</SelectItem>
                        <SelectItem value="martes">Martes</SelectItem>
                        <SelectItem value="miercoles">Miércoles</SelectItem>
                        <SelectItem value="jueves">Jueves</SelectItem>
                        <SelectItem value="viernes">Viernes</SelectItem>
                        <SelectItem value="sabado">Sábado</SelectItem>
                        <SelectItem value="domingo">Domingo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fecha">Fecha</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-fecha"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hora">Hora</Label>
                    <Input
                      id="hora"
                      type="time"
                      value={formData.hora}
                      onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-hora"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_participantes">Máx. Participantes</Label>
                    <Input
                      id="max_participantes"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.max_participantes}
                      onChange={(e) => setFormData({ ...formData, max_participantes: parseInt(e.target.value) || 12 })}
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-max-participantes"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ubicacion">Ubicación</Label>
                    <Select
                      value={formData.ubicacion_id || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, ubicacion_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-ubicacion">
                        <SelectValue placeholder="Sin ubicación" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="none">Sin ubicación</SelectItem>
                        {ubicaciones.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.ubicacion_id === 'otros' && (
                      <div className="space-y-2 mt-2 p-2 bg-gray-800/50 rounded border border-gray-700">
                        <Input
                          value={formData.ubicacion_nombre_custom}
                          onChange={(e) => setFormData({ ...formData, ubicacion_nombre_custom: e.target.value })}
                          placeholder="Nombre del sitio (ej. Playa de Magaluf)"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                          data-testid="input-ubi-custom-nombre"
                        />
                        <Input
                          value={formData.ubicacion_maps_custom}
                          onChange={(e) => setFormData({ ...formData, ubicacion_maps_custom: e.target.value })}
                          placeholder="Enlace de Google Maps (opcional)"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                          data-testid="input-ubi-custom-maps"
                        />
                      </div>
                    )}
                  </div>
                  {/* Sección Acertijo opcional */}
                  <details className="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-purple-300 hover:bg-gray-800 flex items-center gap-2">
                      🎮 Añadir reto / acertijo (opcional)
                    </summary>
                    <div className="p-3 space-y-3 border-t border-gray-700">
                      <div className="space-y-1">
                        <Label htmlFor="acertijo_enunciado" className="text-xs">Enunciado del acertijo</Label>
                        <Textarea
                          id="acertijo_enunciado"
                          rows={2}
                          value={formData.acertijo_enunciado}
                          onChange={(e) => setFormData({ ...formData, acertijo_enunciado: e.target.value })}
                          placeholder="Ej. Animal con tenazas que camina de lado"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                          data-testid="input-acertijo-enunciado"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="acertijo_respuesta" className="text-xs">Respuesta correcta</Label>
                        <Input
                          id="acertijo_respuesta"
                          value={formData.acertijo_respuesta}
                          onChange={(e) => setFormData({ ...formData, acertijo_respuesta: e.target.value })}
                          placeholder="cangrejo"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                          data-testid="input-acertijo-respuesta"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="acertijo_consecuencia" className="text-xs">Consecuencia por fallar</Label>
                        <Input
                          id="acertijo_consecuencia"
                          value={formData.acertijo_consecuencia}
                          onChange={(e) => setFormData({ ...formData, acertijo_consecuencia: e.target.value })}
                          placeholder="20 burpees"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                          data-testid="input-acertijo-consecuencia"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500">El reto se cierra automáticamente 2 horas antes del entreno. Los apuntados recibirán una notificación.</p>
                    </div>
                  </details>
                  <Button type="submit" className="w-full btn-primary-mk" data-testid="btn-save-training">
                    Crear Entreno
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Selector de mes para la vista */}
            <div className="flex items-center justify-center gap-4 mb-8 p-4 bg-gray-800/50 rounded-xl">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                className="text-white hover:text-gold hover:bg-gray-700"
                data-testid="btn-view-mes-anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <span className="text-2xl font-bold text-gold capitalize min-w-[200px] text-center">
                {format(viewMonth, 'MMMM yyyy', { locale: es })}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className="text-white hover:text-gold hover:bg-gray-700"
                data-testid="btn-view-mes-siguiente"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>

            {filteredTrainings.length === 0 ? (
              <div className="text-center py-20">
                <Calendar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay entrenamientos para {format(viewMonth, 'MMMM yyyy', { locale: es })}</p>
                <p className="text-gray-500 text-sm mt-2">Usa &ldquo;Crear Mes Completo&rdquo; para añadir entrenamientos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTrainings.map((training) => (
                  <Card 
                    key={training.id} 
                    className={`card-mk hover:border-gold/50 transition-colors cursor-pointer ${
                      selectedTrainings.has(training.id) ? 'ring-2 ring-gold border-gold' : ''
                    }`}
                    data-testid={`admin-training-card-${training.id}`}
                    onClick={() => isSelectionMode && toggleTrainingSelection(training.id)}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          {isSelectionMode && (
                            <Checkbox
                              checked={selectedTrainings.has(training.id)}
                              onCheckedChange={() => toggleTrainingSelection(training.id)}
                              className="mt-1 border-gray-500 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                              data-testid={`checkbox-training-${training.id}`}
                            />
                          )}
                          <div>
                            <span className="capitalize text-lg">{training.dia_semana}</span>
                            <div className="text-sm text-gold font-normal mt-1">
                              {format(new Date(training.fecha + 'T00:00:00'), "d 'de' MMMM", { locale: es })}
                            </div>
                          </div>
                        </div>
                        {!isSelectionMode && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleEditTraining(training)}
                              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gold hover:bg-gray-700 rounded"
                              data-testid={`btn-edit-training-${training.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTraining(training.id, training.fecha)}
                              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded"
                              data-testid={`btn-delete-training-${training.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-0">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Tipo:</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            training.tipo === 'especial' && training.color ? 'text-white' :
                            training.tipo === 'carrera' ? 'bg-blue-500/20 text-blue-400' :
                            training.tipo === 'funcional' ? 'bg-yellow-500/20 text-yellow-400' :
                            training.tipo === 'fuerza' ? 'bg-amber-500/20 text-amber-400' :
                            training.tipo === 'competicion' ? 'bg-red-500/20 text-red-400' :
                            training.tipo === 'especial' ? 'bg-pink-500/20 text-pink-400' :
                            training.tipo === 'carrera_mediciones' ? 'bg-purple-500/20 text-purple-400' :
                            training.tipo === 'excursion' ? 'bg-green-500/20 text-green-400' :
                            training.tipo === 'natacion' ? 'bg-cyan-500/20 text-cyan-400' :
                            training.tipo === 'bike' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}
                          style={training.tipo === 'especial' && training.color ? { backgroundColor: training.color } : undefined}
                        >
                          {training.tipo === 'natacion' ? 'Natacion' : training.tipo === 'bike' ? 'Bike' : training.tipo === 'fuerza' ? 'Fuerza' : training.tipo === 'carrera_mediciones' ? 'Carrera + Mediciones' : training.tipo}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Hora:</span>
                        <span className="text-white">{training.hora}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Participantes:</span>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-gray-400" />
                          <span className={`${training.participantes.length >= training.max_participantes ? 'text-red-400' : 'text-white'}`}>
                            {training.participantes.length}/{training.max_participantes}
                          </span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-700">
                        {training.plazas_abiertas ? (
                          <span className="text-xs text-green-500 flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Plazas abiertas
                          </span>
                        ) : (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            Plazas cerradas
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Diálogo de edición */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditingTraining(null);
            resetForm();
          }
        }}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold">
                Editar Entreno - {editingTraining && format(new Date(editingTraining.fecha), "d 'de' MMMM", { locale: es })}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateTraining} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tipo">Tipo de Entreno</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value, color: value === 'especial' ? formData.color : '' })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="edit-select-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="carrera">Carrera</SelectItem>
                    <SelectItem value="funcional">Funcional</SelectItem>
                    <SelectItem value="fuerza">Fuerza</SelectItem>
                    <SelectItem value="competicion">Competicion</SelectItem>
                    <SelectItem value="carrera_mediciones">Carrera + Mediciones</SelectItem>
                    <SelectItem value="especial">Especial</SelectItem>
                    <SelectItem value="excursion">Excursion</SelectItem>
                    <SelectItem value="natacion">Natacion</SelectItem>
                    <SelectItem value="bike">Bike</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tipo === 'especial' && (
                  <div className="mt-2 p-2 bg-gray-800/60 rounded-lg border border-gray-700 space-y-2" data-testid="edit-especial-color-picker">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-gray-400">Color del badge (Especial)</Label>
                      {formData.color && (
                        <button type="button" onClick={() => setFormData({ ...formData, color: '' })}
                          className="text-[10px] text-gray-500 hover:text-white underline"
                          data-testid="edit-btn-color-reset">Restablecer</button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['#a855f7','#ec4899','#f59e0b','#ef4444','#22c55e','#06b6d4','#3b82f6','#fbbf24','#8b5cf6'].map(c => (
                        <button key={c} type="button"
                          onClick={() => setFormData({ ...formData, color: c })}
                          className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                            (formData.color || '#a855f7') === c ? 'border-white ring-2 ring-white/40 scale-110' : 'border-gray-600'
                          }`}
                          style={{ backgroundColor: c }}
                          title={c}
                          data-testid={`edit-color-preset-${c.replace('#','')}`}
                        />
                      ))}
                      <input type="color"
                        value={formData.color || '#a855f7'}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-7 h-7 rounded-full bg-transparent border border-gray-600 cursor-pointer p-0"
                        data-testid="edit-input-color-custom"
                        title="Color personalizado"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Entreno del día (solo lo ves tú)</Label>
                <Select
                  value={formData.entreno_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, entreno_id: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-entreno-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                    <SelectItem value="none">Sin entreno asignado</SelectItem>
                    {entrenosList.map(en => (
                      <SelectItem key={en.id} value={en.id}>{en.codigo} — {en.dios}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.tipo === 'competicion' && (
                <div className="space-y-2" data-testid="texto-competicion-edit">
                  <Label htmlFor="edit-texto-competicion" className="text-red-300">Texto para el calendario</Label>
                  <Input
                    id="edit-texto-competicion"
                    value={formData.texto_competicion}
                    onChange={(e) => setFormData({ ...formData, texto_competicion: e.target.value })}
                    placeholder="Ej: Travesía Cap Blanc"
                    className="bg-gray-800 border-red-700/40 text-white"
                    maxLength={60}
                    data-testid="edit-input-texto-competicion"
                  />
                  <p className="text-xs text-gray-500">Se muestra en la tarjeta del calendario (máx. 60 caracteres)</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="edit-hora">Hora</Label>
                <Input
                  id="edit-hora"
                  type="time"
                  value={formData.hora}
                  onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-hora"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-fecha">Fecha</Label>
                <Input
                  id="edit-fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-fecha"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-max-participantes">Máx. Participantes</Label>
                <Input
                  id="edit-max-participantes"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_participantes}
                  onChange={(e) => setFormData({ ...formData, max_participantes: parseInt(e.target.value) || 12 })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-max-participantes"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ubicacion">Ubicación</Label>
                <Select
                  value={formData.ubicacion_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, ubicacion_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="edit-select-ubicacion">
                    <SelectValue placeholder="Sin ubicación" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Sin ubicación</SelectItem>
                    {ubicaciones.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.ubicacion_id === 'otros' && (
                  <div className="space-y-2 mt-2 p-2 bg-gray-800/50 rounded border border-gray-700">
                    <Input
                      value={formData.ubicacion_nombre_custom}
                      onChange={(e) => setFormData({ ...formData, ubicacion_nombre_custom: e.target.value })}
                      placeholder="Nombre del sitio"
                      className="bg-gray-900 border-gray-700 text-white text-sm"
                      data-testid="edit-input-ubi-custom-nombre"
                    />
                    <Input
                      value={formData.ubicacion_maps_custom}
                      onChange={(e) => setFormData({ ...formData, ubicacion_maps_custom: e.target.value })}
                      placeholder="Enlace de Google Maps (opcional)"
                      className="bg-gray-900 border-gray-700 text-white text-sm"
                      data-testid="edit-input-ubi-custom-maps"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  data-testid="btn-cancel-edit"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 btn-primary-mk" data-testid="btn-save-edit">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminTrainings;
